/**
 * browser-loop — the five-ID model.
 *
 * Closed-loop §III.8.1 prescribes a four-tier ID hierarchy (we add a
 * fifth, `user_id`, sourced from the JWT subject when authed):
 *
 *   device_id   — localStorage UUIDv7, forever, anonymous device key
 *   session_id  — sessionStorage UUIDv7, per-tab-lifetime
 *   tab_id      — sessionStorage UUIDv7, same lifetime as session_id
 *   pageview_id — in-memory UUIDv7, regenerated on every navigation
 *   event_id    — per-event UUIDv7, idempotency key
 *
 * UUIDv7 is time-ordered so the device_id sorts globally, the session_id
 * sorts within a device, etc. — makes ad-hoc SurQL queries naturally
 * chronological without ORDER BY tricks.
 *
 * Storage discipline:
 *   - device_id        → localStorage (survives tab close, profile-scoped)
 *   - session_id, tab_id → sessionStorage (per-tab, dies on tab close)
 *   - pageview_id      → in-memory only (regenerated on every nav)
 *
 * Why pageview_id is in-memory: the answer to "what happened on THIS
 * specific page render" needs an id that resets on navigate. Storing it
 * anywhere persistent would leak across navs.
 */

const DEVICE_KEY = "hh_browser_loop_device_id";
const SESSION_KEY = "hh_browser_loop_session_id";
const TAB_KEY = "hh_browser_loop_tab_id";

/**
 * UUIDv7 — time-prefixed UUID. 48 bits unix-ms epoch + 74 bits random.
 * Sorts chronologically as a string, lexicographically equal to time order.
 * Pure browser implementation — no crypto subtle, no external dep.
 */
function uuidv7(): string {
  const now = Date.now();
  // Unix-ms in 48 bits, then 74 random bits.
  const buf = new Uint8Array(16);
  // Random fill for the whole buffer first.
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < 16; i++) buf[i] = Math.floor(Math.random() * 256);
  }
  // Overwrite first 6 bytes with the timestamp.
  buf[0] = (now / 2 ** 40) & 0xff;
  buf[1] = (now / 2 ** 32) & 0xff;
  buf[2] = (now / 2 ** 24) & 0xff;
  buf[3] = (now / 2 ** 16) & 0xff;
  buf[4] = (now / 2 ** 8) & 0xff;
  buf[5] = now & 0xff;
  // Set version (7) and variant (10) bits per RFC.
  buf[6] = (buf[6] & 0x0f) | 0x70;
  buf[8] = (buf[8] & 0x3f) | 0x80;

  const hex = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16,
  )}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

function readOrCreate(
  storage: Storage | null,
  key: string,
  factory: () => string,
): string {
  if (!storage) return factory();
  try {
    const existing = storage.getItem(key);
    if (existing && existing.length > 0) return existing;
    const fresh = factory();
    storage.setItem(key, fresh);
    return fresh;
  } catch {
    // Private mode / SecurityError → ephemeral id, lives in this closure only.
    return factory();
  }
}

let _pageviewId: string | null = null;

export interface BrowserIdentity {
  deviceId: string;
  sessionId: string;
  tabId: string;
  pageviewId: string;
}

export function getIdentity(): BrowserIdentity {
  if (typeof window === "undefined") {
    // SSR: synthesize ephemerals — never used because client never runs on SSR.
    return {
      deviceId: uuidv7(),
      sessionId: uuidv7(),
      tabId: uuidv7(),
      pageviewId: uuidv7(),
    };
  }
  const deviceId = readOrCreate(window.localStorage, DEVICE_KEY, uuidv7);
  const sessionId = readOrCreate(window.sessionStorage, SESSION_KEY, uuidv7);
  const tabId = readOrCreate(window.sessionStorage, TAB_KEY, uuidv7);
  const pageviewId = _pageviewId ?? (_pageviewId = uuidv7());
  return { deviceId, sessionId, tabId, pageviewId };
}

/** Called by the nav patch on every pushstate/popstate/replacestate. */
export function rotatePageview(): string {
  _pageviewId = uuidv7();
  return _pageviewId;
}

export function newEventId(): string {
  return uuidv7();
}

// Internal export for tests / probes that need to construct a UUIDv7.
export { uuidv7 };
